import { Injectable, inject } from '@angular/core';
import { PolicyRequestsCommonService, PolicyRequestsData } from '@nationwide/dgs-internet-servicing-policy-requests';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { DistributionPartnerIndividualInfoService } from '../../shared/services/distribution-partner-individual-info/distribution-partner-individual-info.service';
import { CoveredLocationResponse } from '../../tpi-models/covered-locations';
import { BillingAccountRelatedAccounts } from '../../tpi-models/general/billing-account-related-accounts';
import { CoveragesResponse } from '../../tpi-models/general/coverages-models';
import { MortgageInformationResponse } from '../../tpi-models/general/mortgage-company';
import { PolicyInformationResponse } from '../../tpi-models/general/policy-information';
import { BillingMappingResponse } from '../../tpi-models/general/property-endorsement';
import { PropertySearchValues } from '../../tpi-models/general/search-values';
import { LoggerService } from '../logger/logger.service';
import { BillingService } from './billing.service';
import { PolicySearchResultsAdapterService } from './policy-search-results-adapter.service';
import { RenewalsService } from './renewals.service';
import { PolicyHolders } from '../../tpi-models/policy-requests';
import { DpimRoleInfoResponse } from '../../tpi-models/dpim';

@Injectable({
    providedIn: 'root'
})
export class PolicySearchResultsService {
    private billingServices = inject(BillingService);
    private distributionPartnerIndividualInfoService = inject(DistributionPartnerIndividualInfoService);
    private loggerService = inject(LoggerService);
    private policyRequestsCommonService = inject(PolicyRequestsCommonService);
    private renewalsService = inject(RenewalsService);
    private searchResultsAdapter = inject(PolicySearchResultsAdapterService);

    getPropertySearchResults(searchValues: PropertySearchValues, consumerData: any, euaToken: any): Observable<any> {
        const policyNumber = searchValues.policyNumber;
        consumerData.isDirectCallToPC = true;

        const zipCode = searchValues.zipCode;
        const policyRequestsData: PolicyRequestsData = {
            consumerData,
            productType: searchValues.productType,
            policyId: policyNumber,
            skipCache: true
        };
        const initApiCalls = [
            this.policyRequestsCommonService.getPolicy(policyRequestsData).pipe(
                switchMap((policyInformationResponse: PolicyInformationResponse) => {
                    const policyInformationMapping = this.searchResultsAdapter.mapPropertyPolicyInformation(policyInformationResponse, {
                        policyNumber,
                        zipCode,
                        productType: searchValues.productType
                    });
                    return of(policyInformationMapping);
                }),
                catchError((error) => {
                    this.loggerService.error(
                        'Error In  Retrieve Policy Information API - getPolicy()',
                        {
                            policyNumber,
                            error,
                            productType: searchValues.productType
                        }
                    );
                    return of(error);
                })
            ),
            this.policyRequestsCommonService.getPolicyHolders(policyRequestsData).pipe(switchMap(
                (policyInsured: PolicyHolders[]) => of(this.searchResultsAdapter.mapPropertyPolicyInsured(policyInsured, { policyNumber, zipCode, productType: searchValues.productType }))
            ), catchError((error) => {
                this.loggerService.error(
                    'Error In  Retrieve Policy Holder API - getPolicyHolders()',
                    {
                        policyNumber,
                        error
                    }
                );
                return of(error);
            })),
            this.policyRequestsCommonService.getPolicyMortgages(policyRequestsData).pipe(switchMap(
                (mortgageResponse: MortgageInformationResponse[]) => of(this.searchResultsAdapter.mapMortgageInformation(mortgageResponse, { policyNumber, zipCode, productType: searchValues.productType }))
            ), catchError((error) => {
                this.loggerService.error(
                    'Error In  Fetch  API - getPolicyMortgages()',
                    {
                        policyNumber,
                        error,
                        productType: searchValues.productType
                    }
                );
                return of(error);
            })),
            this.policyRequestsCommonService.getPolicyCoverages(policyRequestsData).pipe(switchMap((coveragesResponse: CoveragesResponse) => of(this.searchResultsAdapter.mapPropertyEndorsements(coveragesResponse, searchValues.productType))
            ), catchError((error) => {
                this.loggerService.error(
                    'Error In  Fetch  API - getPolicyCoverages()',
                    {
                        policyNumber,
                        error,
                        productType: searchValues.productType
                    }
                );
                return of(error);
            })),
            this.distributionPartnerIndividualInfoService.fetchDistributionPartnerIndividualInfo(policyNumber, euaToken).pipe(switchMap(
                (dpiResponse: DpimRoleInfoResponse) => of(this.searchResultsAdapter.mapAgentInformation(dpiResponse, { policyNumber, zipCode, productType: searchValues.productType }))
            ), catchError((error) => {
                this.loggerService.error(
                    'Error In  Fetch  API - fetchDistributionPartnerIndividualInfo()',
                    {
                        policyNumber,
                        error,
                        productType: searchValues.productType
                    }
                );
                return of(error);
            })),
            this.policyRequestsCommonService.getPolicyCoveredLocations(policyRequestsData).pipe(
                switchMap((coveredLocationResponse: CoveredLocationResponse) => of(this.searchResultsAdapter.mapCoveredLocation(coveredLocationResponse))), catchError((error) => {
                    this.loggerService.error(
                        'Error In  Fetch  API - getPolicyCoveredLocations()',
                        {
                            policyNumber,
                            error,
                            productType: searchValues.productType
                        }
                    );
                    return of(error);
                })),
            this.billingServices.getBillingRelatedAccounts(policyNumber).pipe(
                switchMap((response: BillingAccountRelatedAccounts) => {
                    const billingAccountNumber: string = this.billingServices.getBillingNumberFromRelatedAccountsResponse(response);
                    this.loggerService.info(`Billing Related Accounts for ${policyNumber} :  ${billingAccountNumber}`);
                    if (billingAccountNumber) {
                        const billingcalls = [
                            this.billingServices.getBillingAcountBills(billingAccountNumber),
                            this.billingServices.getBillingAcountBalances(billingAccountNumber)
                        ];
                        return forkJoin(billingcalls);
                    }
                    return of(null);
                }),
                switchMap((response: any[]): Observable<BillingMappingResponse> => {
                    if (response?.length > 0) {
                        response.push({ policyNumber });
                    }
                    return of(this.searchResultsAdapter.mapBillingResponse(response));
                }),
                catchError((error) => {
                    this.loggerService.error('Error In Billing Related Accounts API - getBillingRelatedAccounts()', { policyNumber, error });
                    return of(error);
                })),
            this.renewalsService.getPropertyRenewals(policyNumber, consumerData, { propertyType: searchValues.productType, skipCache: true }).pipe(
                switchMap((response) => of(response)),
                catchError((error) => {
                    this.loggerService.error('Error In getPropertyRenewals API - getPropertyRenewals()', { policyNumber, error });
                    return of(error);
                }))
        ];
        return forkJoin(initApiCalls).pipe(
            switchMap((response) => of(response)));
    }
}